import type { TFunction } from "i18next";

import {
  buildDemoUnits,
  buildUnitWatchStates,
} from "../../../Layouts/monitoreo-ia/services/centinelaMocks";
import type { WatchStatus } from "../../../Layouts/monitoreo-ia/types";
import { buildGreeting, type WelcomeContext } from "./welcome";

export const SIMULATED_INTENTS = [
  "stopped",
  "offline",
  "speeding",
  "summary",
  "geofences",
  "trips",
  "calls",
  "greeting",
  "fallback",
] as const;

export type SimulatedIntent = (typeof SIMULATED_INTENTS)[number];

const INTENT_KEYWORDS: Record<Exclude<SimulatedIntent, "fallback">, readonly string[]> = {
  stopped: ["detenid", "parad", "stopped", "idle", "sin moverse"],
  offline: ["senal", "signal", "gps", "offline", "desconect", "jamming"],
  speeding: ["velocidad", "speed", "exceso", "rapido", "fast"],
  summary: ["resumen", "summary", "como va", "how is", "estado de la flota", "fleet status"],
  geofences: ["geocerca", "geofence", "zona", "area"],
  trips: ["viaje", "trip", "ruta", "route", "custodia", "escort"],
  calls: ["llamad", "call", "protocolo", "protocol", "verificacion", "verification"],
  greeting: ["hola", "buenas", "hello", "hi ", "hey", "buenos dias", "buenas tardes"],
};

const DIACRITICS = /[̀-ͯ]/g;

const normalize = (value: string) =>
  value.toLowerCase().normalize("NFD").replace(DIACRITICS, "");

export const detectSimulatedIntent = (text: string): SimulatedIntent => {
  const value = `${normalize(text)} `;

  const match = SIMULATED_INTENTS.find(
    (intent) =>
      intent !== "fallback" &&
      INTENT_KEYWORDS[intent as Exclude<SimulatedIntent, "fallback">].some((keyword) =>
        value.includes(keyword),
      ),
  );

  return match ?? "fallback";
};

interface DemoSnapshot {
  total: number;
  byStatus: Record<WatchStatus, string[]>;
  speeding: { name: string; speed: number }[];
  incidents: { name: string; note: string }[];
  calls: number;
}

const snapshotOf = (t: TFunction): DemoSnapshot => {
  const units = buildDemoUnits();
  const refs = units.map((unit) => ({ id: unit.id, name: unit.name }));
  const states = buildUnitWatchStates(t, refs);

  const byStatus: Record<WatchStatus, string[]> = {
    safe: [],
    warning: [],
    alert: [],
    risk: [],
    offline: [],
    sin_custodia: [],
  };

  states.forEach((state, index) => byStatus[state.status].push(refs[index].name));

  return {
    total: refs.length,
    byStatus,
    speeding: states
      .map((state, index) => ({ name: refs[index].name, speed: state.speedKph ?? 0 }))
      .filter((entry) => entry.speed >= 95)
      .slice(0, 3),
    incidents: states
      .map((state, index) => ({
        name: refs[index].name,
        note: state.status === "safe" ? null : state.aiMessage,
      }))
      .filter((entry): entry is { name: string; note: string } => Boolean(entry.note))
      .slice(0, 3),
    calls: states.filter((state) => state.status !== "safe").length,
  };
};

const listOf = (names: string[]) => names.join(", ");

export function buildSimulatedReply(text: string, t: TFunction): string {
  const intent = detectSimulatedIntent(text);
  const snapshot = snapshotOf(t);
  const key = `monitoreoIa.chat.simulated.${intent}`;

  if (intent === "stopped") {
    const stopped = [...snapshot.byStatus.warning, ...snapshot.byStatus.alert];
    return stopped.length === 0
      ? t("monitoreoIa.chat.simulated.stoppedNone")
      : t(key, { count: stopped.length, units: listOf(stopped.slice(0, 4)) });
  }

  if (intent === "offline") {
    const offline = snapshot.byStatus.offline;
    return offline.length === 0
      ? t("monitoreoIa.chat.simulated.offlineNone")
      : t(key, { count: offline.length, units: listOf(offline) });
  }

  if (intent === "speeding") {
    return snapshot.speeding.length === 0
      ? t("monitoreoIa.chat.simulated.speedingNone")
      : t(key, {
          count: snapshot.speeding.length,
          units: snapshot.speeding
            .map((entry) => `${entry.name} (${entry.speed} km/h)`)
            .join(", "),
        });
  }

  if (intent === "summary") {
    return t(key, {
      total: snapshot.total,
      safe: snapshot.byStatus.safe.length,
      warning: snapshot.byStatus.warning.length,
      alert: snapshot.byStatus.alert.length,
      offline: snapshot.byStatus.offline.length,
      calls: snapshot.calls,
    });
  }

  if (intent === "calls") {
    return t(key, { calls: snapshot.calls, incidents: snapshot.incidents.length });
  }

  if (intent === "trips" || intent === "geofences" || intent === "greeting") {
    return t(key, { total: snapshot.total });
  }

  const incident = snapshot.incidents[0];
  return incident
    ? t("monitoreoIa.chat.simulated.fallbackIncident", {
        unit: incident.name,
        note: incident.note,
      })
    : t("monitoreoIa.chat.simulated.fallback");
}

export function buildWelcome(context: WelcomeContext, t: TFunction, name?: string): string {
  const snapshot = snapshotOf(t);
  const moving = snapshot.byStatus.safe.length + snapshot.byStatus.alert.length;

  const body = t(`monitoreoIa.chat.welcome.${context}`, {
    total: snapshot.total,
    moving,
    stopped: snapshot.byStatus.warning.length,
    alerts: snapshot.byStatus.alert.length,
    warnings: snapshot.byStatus.warning.length,
    safe: snapshot.byStatus.safe.length,
    offline: snapshot.byStatus.offline.length,
    calls: snapshot.calls,
  });

  return `${buildGreeting(t, name)}\n\n${body}`;
}
